import {
  BREAKPOINTS, STYLE_PROPERTIES,
  type Breakpoint, type ElementStylesDto, type StyleBlockDto, type StyleValueDto, type ThemeStylesDto
} from '@xshop/shared';
import { breakpointBand, breakpointMedia, styleLayers } from './styleCascade';
import { styleDeclaration, type StyleDrop } from './styleDeclaration';
import { hashStyleText } from './styleHash';

// An element's styles, compiled into the one class it wears and the rules that class stands for.
//
// Nothing here is an inline style, and that is the point: a `style` attribute cannot hold `:hover` and it
// cannot hold a media query, so an engine that emits inline styles can only ever draw a third of what the
// inspector offers. What comes out is a scoped rule set — `.xs-<hash>.xs-<hash>`, the same again with a
// state on the end, and each of those again inside a width's media query — keyed by a hash of its own
// text, so a page full of identical buttons carries one rule between them.
//
// Every rule wears the class twice. That is what lets a rule for the phone override one for the desktop
// without a state rule quietly outranking both: a resting rule scores two classes, a state rule three, and
// nothing else in the sheet can come between them.

/** What an element's styles compile to: the class to wear, the rules to write, and what fell away on the way. */
export type CompiledElementStyles = { className: string; css: string; drops: readonly StyleDrop[] };

/** Everything the engine needs to write one element's rules: the store's tokens, its styles, the widths it hides at. */
export type ElementStyleInput = {
  theme: ThemeStylesDto;
  styles?: ElementStylesDto;
  hiddenOn?: readonly Breakpoint[];
};

type StyleRule = { media: string | null; suffix: string; body: string };

/** What each state appends to the element's doubled class. `default` appends nothing. */
const STATE_SUFFIX = { default: '', hover: ':hover', focus: ':focus', active: ':active' } as const;

/** The schema's own order, so a block's declarations are written the same way whatever order they arrived in. */
const PROPERTY_ORDER: ReadonlyMap<string, number> = new Map(STYLE_PROPERTIES.map((name, index) => [name as string, index]));

// Writes one block's declarations in the schema's order, collecting whatever was dropped along the way.
function blockBody(block: StyleBlockDto, theme: ThemeStylesDto, drops: StyleDrop[]): string {
  const entries = Object.entries(block) as Array<[string, StyleValueDto]>;
  entries.sort((left, right) => (PROPERTY_ORDER.get(left[0]) ?? STYLE_PROPERTIES.length) - (PROPERTY_ORDER.get(right[0]) ?? STYLE_PROPERTIES.length));
  const written: string[] = [];
  for (const [property, value] of entries) {
    const { css, drop } = styleDeclaration(property, value, theme);
    if (css !== null) written.push(css);
    if (drop !== null) drops.push(drop);
  }
  return written.join(';');
}

// Turns each layer of the cascade into the rule it writes, skipping the ones whose declarations all fell away.
function styleRules(styles: ElementStylesDto | undefined, theme: ThemeStylesDto, drops: StyleDrop[]): StyleRule[] {
  const rules: StyleRule[] = [];
  for (const layer of styleLayers(styles)) {
    const body = blockBody(layer.block, theme, drops);
    if (body !== '') rules.push({ media: breakpointMedia(layer.breakpoint), suffix: STATE_SUFFIX[layer.state], body });
  }
  return rules;
}

// Switches the element off in each band the merchant hid it at, last and with weight so no state undoes it.
function hiddenRules(hiddenOn: readonly Breakpoint[]): StyleRule[] {
  return BREAKPOINTS
    .filter((breakpoint) => hiddenOn.includes(breakpoint))
    .map((breakpoint) => ({ media: breakpointBand(breakpoint), suffix: '', body: 'display:none!important' }));
}

// Serialises the rules with the class left out, which is the text the class name is then the hash of.
function canonicalText(rules: readonly StyleRule[]): string {
  return rules.map((rule) => `${rule.media ?? ''}|${rule.suffix}|${rule.body}`).join('\n');
}

// Writes the rules out under their class, folding a run that shares a media query into a single block.
function renderRules(rules: readonly StyleRule[], className: string): string {
  const selector = `.${className}.${className}`;
  let css = '';
  let open: string | null = null;
  for (const rule of rules) {
    if (rule.media !== open) {
      if (open !== null) css += '}';
      if (rule.media !== null) css += `@media ${rule.media}{`;
      open = rule.media;
    }
    css += `${selector}${rule.suffix}{${rule.body}}`;
  }
  return open === null ? css : `${css}}`;
}

// Compiles one element's styles into the single class it wears and the rules that class stands for.
export function compileElementStyles(input: ElementStyleInput): CompiledElementStyles {
  const drops: StyleDrop[] = [];
  const rules = [...styleRules(input.styles, input.theme, drops), ...hiddenRules(input.hiddenOn ?? [])];
  if (rules.length === 0) return { className: '', css: '', drops };
  const className = `xs-${hashStyleText(canonicalText(rules))}`;
  return { className, css: renderRules(rules, className), drops };
}
